import { useEffect, useRef } from 'react';
import Supercluster from 'supercluster';
import type { PointFeature } from 'supercluster';
import { Structure } from '../../types';
import { startSpan, endSpan } from '../../utils/mapPerf';
import { MAX_ZOOM } from './types';

// Cluster index type aliases — exported so Map.web.tsx and useMarkerLayer.web.ts
// can share them without re-declaring.
export type StructurePointProps = { structureId: string; address?: string };
export type ClusterInstance = Supercluster<StructurePointProps>;

interface UseClusterIndexParams {
  isLoaded: boolean;
  structuresByShard: Record<string, Structure[]>;
  userAddedStructures: Structure[];
  shardOrderKey: string;
  userAddedSignature: string;
  superclusterRef: React.MutableRefObject<ClusterInstance | null>;
  renderClustersRef: React.MutableRefObject<() => void>;
}

/**
 * Rebuilds the Supercluster index whenever the set of loaded shards changes.
 * This is the only expensive step tied to total point count — O(N log N)
 * for ~80k points is ~50ms. Deferred to idle time so a shard commit
 * landing mid-gesture doesn't stall the pan; if a newer commit arrives
 * before this one has run, we bail out and let the next scheduled
 * rebuild win.
 */
export function useClusterIndex({
  isLoaded,
  structuresByShard,
  userAddedStructures,
  shardOrderKey,
  userAddedSignature,
  superclusterRef,
  renderClustersRef,
}: UseClusterIndexParams): void {
  // Cancellation token — incremented each time a new rebuild is scheduled.
  // If the token has changed by the time idle callback fires, the stale
  // rebuild bails without touching the index.
  const indexBuildTokenRef = useRef(0);

  useEffect(() => {
    if (!isLoaded) return;
    const token = ++indexBuildTokenRef.current;
    const schedule =
      typeof window !== 'undefined' &&
      'requestIdleCallback' in window &&
      typeof (window as unknown as { requestIdleCallback?: (cb: IdleRequestCallback) => number })
        .requestIdleCallback === 'function'
        ? (
            window as unknown as {
              requestIdleCallback: (cb: IdleRequestCallback, opts?: { timeout: number }) => number;
            }
          ).requestIdleCallback
        : (cb: IdleRequestCallback) =>
            window.setTimeout(
              () =>
                cb({
                  didTimeout: false,
                  timeRemaining: () => 16,
                } as IdleDeadline),
              0
            );

    const rebuild = () => {
      if (token !== indexBuildTokenRef.current) return;
      const span = startSpan('cluster.index_rebuild');
      const features: PointFeature<StructurePointProps>[] = [];
      const seen = new Set<string>();
      for (const key in structuresByShard) {
        const arr = structuresByShard[key];
        for (let i = 0; i < arr.length; i++) {
          const s = arr[i];
          seen.add(s.id);
          features.push({
            type: 'Feature',
            properties: { structureId: s.id, address: s.address },
            geometry: { type: 'Point', coordinates: [s.centroid.longitude, s.centroid.latitude] },
          });
        }
      }
      // Splice in live user-added structures that aren't already in the
      // shard cache (a later re-export will eventually represent them).
      for (let i = 0; i < userAddedStructures.length; i++) {
        const s = userAddedStructures[i];
        if (seen.has(s.id)) continue;
        seen.add(s.id);
        features.push({
          type: 'Feature',
          properties: { structureId: s.id, address: s.address },
          geometry: { type: 'Point', coordinates: [s.centroid.longitude, s.centroid.latitude] },
        });
      }
      if (token !== indexBuildTokenRef.current) {
        endSpan(span);
        return;
      }
      if (!superclusterRef.current) {
        superclusterRef.current = new Supercluster({
          radius: 60,
          maxZoom: MAX_ZOOM,
          minPoints: 3,
        });
        superclusterRef.current.load([]);
      }
      superclusterRef.current.load(features);
      endSpan(span);
      // Cluster markers are keyed by rounded position + count bucket, so
      // they survive the index rebuild when they land in the same spot.
      // Point markers are keyed by structureId and always survive.
      if (token !== indexBuildTokenRef.current) return;
      requestAnimationFrame(() => {
        if (token !== indexBuildTokenRef.current) return;
        renderClustersRef.current();
      });
    };
    schedule(rebuild, { timeout: 200 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shardOrderKey, userAddedSignature, isLoaded]);
}
